import {
  CANONICAL_IMPORT_FIELDS,
  CANONICAL_IMPORT_FIELD_MAP
} from '../constants/contactImportFields.js';

export function normalizeMappingLabel(value = '') {
  return String(value || '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getFieldMatchStrings(field) {
  return [
    field.key,
    field.label,
    ...(field.synonyms || [])
  ].map(normalizeMappingLabel).filter(Boolean);
}

function buildReasons({ labelExactMatch, synonymExactMatch, normalizedContainsMatch, heuristics }) {
  const reasons = [];
  if (labelExactMatch) reasons.push('exact label match');
  if (synonymExactMatch) reasons.push('known synonym match');
  if (normalizedContainsMatch) reasons.push('normalized label overlap');
  if (heuristics.length > 0) reasons.push(...heuristics);
  return reasons;
}

function getHeuristicBoost(column, field) {
  const hints = column?.patternHints || {};
  const label = normalizeMappingLabel(column?.resolvedLabel || column?.originalLabel || '');
  const reasons = [];
  let score = 0;

  const boost = (value, reason) => {
    score += value;
    reasons.push(reason);
  };

  if (field.key === 'workEmail' && hints.emailLike) boost(22, 'email-like samples');
  if (field.key === 'personalEmail' && hints.emailLike && /\b(personal|private|home)\b/.test(label)) boost(25, 'personal-email label hint');
  if (field.key === 'linkedinUrl' && hints.linkedinLike) boost(28, 'linkedin-like samples');
  if (field.key === 'phone' && hints.phoneLike) boost(20, 'phone-like samples');
  if (field.key === 'companyDomain' && hints.urlLike) boost(14, 'url-like samples');
  if (field.key === 'companyName' && hints.companyLike) boost(18, 'company-like label');
  if (field.key === 'fullName' && hints.nameLike) boost(16, 'name-like samples');
  if (field.key === 'jobTitle' && hints.titleLike) boost(16, 'title-like label');
  if (field.key === 'location' && hints.locationLike) boost(16, 'location-like label');
  if (field.key === 'department' && /\b(function|department|team)\b/.test(label)) boost(16, 'department/function label');
  if (field.key === 'seniority' && /\b(seniority|level)\b/.test(label)) boost(16, 'seniority label');
  if (field.key === 'tags' && /\b(tag|label)\b/.test(label)) boost(16, 'tags label');
  if (field.key === 'notes' && /\b(notes|comments|description)\b/.test(label)) boost(16, 'notes label');
  if (field.key === 'source' && /\b(source)\b/.test(label)) boost(16, 'source label');
  if (field.key === 'owner' && /\b(owner)\b/.test(label)) boost(16, 'owner label');
  if (field.key === 'stage' && /\b(stage|status)\b/.test(label)) boost(16, 'stage label');

  return { score, reasons };
}

export function getAutoMatchSuggestion(column) {
  const columnLabel = normalizeMappingLabel(column?.resolvedLabel || column?.originalLabel || '');
  const candidates = CANONICAL_IMPORT_FIELDS.map((field) => {
    const fieldStrings = getFieldMatchStrings(field);
    const labelExactMatch = fieldStrings.includes(columnLabel);
    const synonymExactMatch = (field.synonyms || []).map(normalizeMappingLabel).includes(columnLabel);
    const normalizedContainsMatch = fieldStrings.some((match) =>
      match && (match.includes(columnLabel) || columnLabel.includes(match))
    );
    const heuristic = getHeuristicBoost(column, field);

    let score = 0;
    if (normalizeMappingLabel(field.key) === columnLabel) score += 100;
    if (normalizeMappingLabel(field.label) === columnLabel) score += 100;
    if (synonymExactMatch) score += 92;
    if (!labelExactMatch && normalizedContainsMatch) score += 68;
    score += heuristic.score;

    return {
      fieldKey: field.key,
      label: field.label,
      confidence: score >= 95 ? 'high' : score >= 72 ? 'medium' : score >= 52 ? 'low' : 'none',
      score,
      reasons: buildReasons({
        labelExactMatch,
        synonymExactMatch,
        normalizedContainsMatch,
        heuristics: heuristic.reasons
      })
    };
  }).sort((a, b) => b.score - a.score);

  const best = candidates[0];
  if (!best || best.confidence === 'none') {
    return {
      fieldKey: null,
      confidence: 'none',
      score: 0,
      reasons: []
    };
  }

  return best;
}

function getAssignedKeys(stateByColumn) {
  return Object.values(stateByColumn)
    .filter((entry) => entry.state === 'mapped' && entry.targetFieldKey)
    .map((entry) => entry.targetFieldKey);
}

export function buildInitialMappingState(detectedColumns = []) {
  const suggestions = detectedColumns.map((column) => ({
    columnIndex: column.columnIndex,
    suggestion: getAutoMatchSuggestion(column)
  })).sort((a, b) => b.suggestion.score - a.suggestion.score);

  const stateByColumn = {};
  const assignedSingleTargets = new Set();

  suggestions.forEach(({ columnIndex, suggestion }) => {
    const field = suggestion.fieldKey ? CANONICAL_IMPORT_FIELD_MAP[suggestion.fieldKey] : null;
    const canAutoMap = field && ['high', 'medium'].includes(suggestion.confidence);
    const targetAlreadyUsed = field && !field.allowMultipleSources && assignedSingleTargets.has(field.key);

    if (canAutoMap && !targetAlreadyUsed) {
      stateByColumn[columnIndex] = {
        state: 'mapped',
        targetFieldKey: field.key,
        source: 'auto',
        confirmed: false,
        confidence: suggestion.confidence,
        suggestion
      };
      if (!field.allowMultipleSources) assignedSingleTargets.add(field.key);
    } else {
      stateByColumn[columnIndex] = {
        state: 'unresolved',
        targetFieldKey: null,
        source: canAutoMap ? 'auto-conflict' : 'none',
        confirmed: false,
        confidence: suggestion.confidence,
        suggestion
      };
    }
  });

  return stateByColumn;
}

export function getFieldUsage(stateByColumn = {}) {
  return getAssignedKeys(stateByColumn).reduce((acc, key) => {
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
}

export function createMappedState(currentState, targetFieldKey, source = 'manual') {
  return {
    ...currentState,
    state: 'mapped',
    targetFieldKey,
    source,
    confirmed: source === 'manual' ? true : currentState?.confirmed || false,
    confidence: source === 'manual'
      ? (currentState?.suggestion?.fieldKey === targetFieldKey ? currentState?.suggestion?.confidence || 'manual' : 'manual')
      : currentState?.confidence || 'medium'
  };
}

export function createIgnoredState(currentState) {
  return {
    ...currentState,
    state: 'ignored',
    targetFieldKey: null,
    source: 'manual',
    confirmed: true
  };
}

export function createUnresolvedState(currentState) {
  return {
    ...currentState,
    state: 'unresolved',
    targetFieldKey: null,
    source: 'manual',
    confirmed: false
  };
}

export function getMappingReadiness(stateByColumn = {}, detectedColumns = []) {
  const mappedFieldKeys = Object.values(stateByColumn)
    .filter((entry) => entry.state === 'mapped' && entry.targetFieldKey)
    .map((entry) => entry.targetFieldKey);

  const mappedFieldSet = new Set(mappedFieldKeys);
  const unresolvedColumns = detectedColumns.filter((column) => stateByColumn[column.columnIndex]?.state === 'unresolved');
  const ignoredColumns = detectedColumns.filter((column) => stateByColumn[column.columnIndex]?.state === 'ignored');
  const mappedColumns = detectedColumns.filter((column) => stateByColumn[column.columnIndex]?.state === 'mapped');
  const lowConfidenceMapped = mappedColumns.filter((column) => {
    const mapping = stateByColumn[column.columnIndex];
    return mapping?.source === 'auto' && mapping?.confidence === 'medium';
  });

  const identityPathways = [
    {
      key: 'workEmail',
      label: 'Work Email',
      satisfied: mappedFieldSet.has('workEmail'),
      missingFields: mappedFieldSet.has('workEmail') ? [] : ['Work Email']
    },
    {
      key: 'linkedinUrl',
      label: 'LinkedIn URL',
      satisfied: mappedFieldSet.has('linkedinUrl'),
      missingFields: mappedFieldSet.has('linkedinUrl') ? [] : ['LinkedIn URL']
    },
    {
      key: 'fullNameCompanyName',
      label: 'Full Name + Company Name',
      satisfied: mappedFieldSet.has('fullName') && mappedFieldSet.has('companyName'),
      missingFields: [
        !mappedFieldSet.has('fullName') && 'Full Name',
        !mappedFieldSet.has('companyName') && 'Company Name'
      ].filter(Boolean)
    },
    {
      key: 'fullNameCompanyDomain',
      label: 'Full Name + Company Domain',
      satisfied: mappedFieldSet.has('fullName') && mappedFieldSet.has('companyDomain'),
      missingFields: [
        !mappedFieldSet.has('fullName') && 'Full Name',
        !mappedFieldSet.has('companyDomain') && 'Company Domain'
      ].filter(Boolean)
    },
    {
      key: 'firstLastCompanyName',
      label: 'First Name + Last Name + Company Name',
      satisfied: mappedFieldSet.has('firstName') && mappedFieldSet.has('lastName') && mappedFieldSet.has('companyName'),
      missingFields: [
        !mappedFieldSet.has('firstName') && 'First Name',
        !mappedFieldSet.has('lastName') && 'Last Name',
        !mappedFieldSet.has('companyName') && 'Company Name'
      ].filter(Boolean)
    },
    {
      key: 'firstLastCompanyDomain',
      label: 'First Name + Last Name + Company Domain',
      satisfied: mappedFieldSet.has('firstName') && mappedFieldSet.has('lastName') && mappedFieldSet.has('companyDomain'),
      missingFields: [
        !mappedFieldSet.has('firstName') && 'First Name',
        !mappedFieldSet.has('lastName') && 'Last Name',
        !mappedFieldSet.has('companyDomain') && 'Company Domain'
      ].filter(Boolean)
    }
  ];

  const identitySatisfied = identityPathways.some((pathway) => pathway.satisfied);

  return {
    identitySatisfied,
    canProceed: identitySatisfied && unresolvedColumns.length === 0,
    mappedCount: mappedColumns.length,
    ignoredCount: ignoredColumns.length,
    unresolvedCount: unresolvedColumns.length,
    lowConfidenceCount: lowConfidenceMapped.length,
    identityPathways,
    unresolvedColumns,
    ignoredColumns,
    mappedColumns,
    missingIdentityMessage: identitySatisfied
      ? null
      : 'Map at least one valid identity pathway before continuing to validation.'
  };
}

export function buildValidationReadyMappingPayload(intakePayload, stateByColumn = {}) {
  const detectedColumns = intakePayload?.detectedColumns || [];
  const readiness = getMappingReadiness(stateByColumn, detectedColumns);

  return {
    importMethod: intakePayload?.importMethod || 'file_upload',
    sourceMeta: {
      file: intakePayload?.file || null,
      sheet: intakePayload?.sheet || null
    },
    mappingAssignments: detectedColumns
      .filter((column) => stateByColumn[column.columnIndex]?.state === 'mapped')
      .map((column) => {
        const mapping = stateByColumn[column.columnIndex];
        return {
          columnIndex: column.columnIndex,
          sourceLabel: column.resolvedLabel,
          normalizedSourceLabel: column.normalizedLabel,
          targetFieldKey: mapping.targetFieldKey,
          targetFieldLabel: CANONICAL_IMPORT_FIELD_MAP[mapping.targetFieldKey]?.label || mapping.targetFieldKey,
          confidence: mapping.confidence,
          source: mapping.source,
          confirmed: mapping.confirmed,
          suggestionReasons: mapping.suggestion?.reasons || []
        };
      }),
    ignoredColumns: detectedColumns
      .filter((column) => stateByColumn[column.columnIndex]?.state === 'ignored')
      .map((column) => ({
        columnIndex: column.columnIndex,
        sourceLabel: column.resolvedLabel
      })),
    unresolvedColumns: detectedColumns
      .filter((column) => stateByColumn[column.columnIndex]?.state === 'unresolved')
      .map((column) => ({
        columnIndex: column.columnIndex,
        sourceLabel: column.resolvedLabel,
        suggestion: stateByColumn[column.columnIndex]?.suggestion?.fieldKey
          ? {
              targetFieldKey: stateByColumn[column.columnIndex].suggestion.fieldKey,
              targetFieldLabel: CANONICAL_IMPORT_FIELD_MAP[stateByColumn[column.columnIndex].suggestion.fieldKey]?.label || stateByColumn[column.columnIndex].suggestion.fieldKey,
              confidence: stateByColumn[column.columnIndex].suggestion.confidence
            }
          : null
      })),
    readiness
  };
}
